import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LineBathIcon from 'LuxKit/Icons/line/LineBathIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import LineVillaIcon from 'LuxKit/Icons/line/LineVillaIcon'
import MarkdownRender from 'LuxKit/MarkdownRender'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { nonNullable } from 'lib/array/arrayUtils'
import { getMaxOccupancies } from 'lib/offer/occupancyUtils'
import { rem } from 'polished'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { getCheapestSelectedPackageOptionWithDefault } from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import usePropertyCategory from 'hooks/usePropertyCategory'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { VILLAS_REVIEWS_TOTAL_THRESHOLD } from 'constants/review'
import Heading from 'LuxKit/Typography/Heading'
import ContactUSP from '../ContactUSP'
import OfferPageLabelGroup from '../OfferPageLabelGroup'
import OfferLocationHeading from '../OfferLocationHeading'
import useFinestLocationSearchUrl from '../useFinestLocationSearchUrl'
import { hasMapCoordinates } from '../NavigableOfferContent/mapCoordinates'
import VillasVipServices from './VillasVipServices'
import ContentTagLabels from 'components/Content/ContentTagLabels'
import { useAppSelector } from 'hooks/reduxHooks'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import { TasteOfGoldPromoCard } from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoStyles'
import TasteOfGoldPromoContent from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoContent'

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(24)};
    grid-template-columns: max-content;
    grid-auto-columns: max-content;
    grid-auto-flow: column;
  }
`

interface Props {
  offer: App.VillaOffer
  onViewLocationClick?: () => void
  onViewReviewsClick: () => void
  pkg: App.HotelPackage
  checkIn: string | undefined
  checkOut: string | undefined
  rooms: Array<App.Occupants>
}

function VillasPageIntro(props: Props) {
  const { offer, onViewLocationClick, onViewReviewsClick, checkIn, checkOut, pkg, rooms } = props
  const intl = useIntl()
  const { property } = offer

  const maxGuests = useMemo(() => {
    const allCapacities = offer.packages.flatMap(p => p.roomRate?.capacities ?? [])
    const max = getMaxOccupancies(allCapacities, { adults: 0 }, {
      maxChildAge: offer.property?.maxChildAge,
      maxInfantAge: offer.property?.maxInfantAge,
      maxTeenagerAge: offer.property?.maxTeenagerAge,
    })
    return max.adults
  }, [offer])

  const propertyCategory = usePropertyCategory(property.category)
  const location = useMemo(() => nonNullable([offer.locationHeading, offer.locationSubheading]).join(', '), [offer])
  const searchUrl = useFinestLocationSearchUrl(offer.id)

  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const showTasteOfGoldPromo = isTasteOfGoldActive && isTasteOfGoldEligibleOffer(
    offer,
    currency,
    offer.property.geoData.countryCode?.toLowerCase() !== 'au',
    pkg.price,
  )

  return (
    <Group fullWidth direction="vertical" gap={20} tabletGap={24}>
      <Group direction="vertical" gap={12}>
        <Group direction="vertical" gap={16}>
          <ContentTagLabels offerId={offer.id} page="offer" />
          <OfferPageLabelGroup offer={offer} />
          <VerticalSpacer gap={12}>
            <OfferLocationHeading
              location={location}
              searchUrl={searchUrl}
              showMapLink={hasMapCoordinates(offer.property)}
              onViewMapClick={onViewLocationClick}
            />
            <Heading variant="heading2" as="h1">{offer.property.name}</Heading>
            <BodyText variant="large">{offer.name}</BodyText>
          </VerticalSpacer>
        </Group>
        {isOfferRatingDisplayable(property.rating, VILLAS_REVIEWS_TOTAL_THRESHOLD) && <OfferRating
          link
          inlineLabel
          variant="small"
          rating={property.rating}
          onReviewSubTitleClick={onViewReviewsClick}
        />}
      </Group>
      <Group direction="vertical" gap={12} tabletGap={16}>
        <Divider kind="primary" />
        <SummaryGrid>
          <BodyTextBlock variant="medium" startIcon={<LineVillaIcon />}>
            {propertyCategory}
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineUsersAltIcon />}>
            {intl.formatMessage(
              {
                id: '04NXgzCA8GcdWpFeUbOX',
                defaultMessage: 'Up to {count, plural, one {# guest} other {# guests}}',
              },
              { count: maxGuests },
            )}
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineBedIcon />}>
            {intl.formatMessage(
              {
                id: 'cgP3UeFNM8LbN-bdgtAm',
                defaultMessage: '{count, plural, one {# bedroom} other {# bedrooms}}',
              },
              { count: offer.property.bedrooms },
            )}
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineBathIcon />}>
            {intl.formatMessage(
              {
                id: '6DMptmbZiWs6lhx7BS90',
                defaultMessage: '{count, plural, one {# bathroom} other {# bathrooms}}',
              },
              { count: offer.property.bathrooms },
            )}
          </BodyTextBlock>
        </SummaryGrid>
        <CSSBreakpoint max="tablet">
          <Group direction="vertical" gap={12} tabletGap={16}>
            <Divider kind="primary" />
            {checkIn && <TAOCancellationPolicies
              offerId={offer.id}
              useDynamicCancellationPolicies={offer.property.useDynamicCancellationPolicies && !pkg.ignoreDynamicCancellationPolicy}
              timezoneOffset={offer.property.timezoneOffset}
              uniqueKey={pkg.uniqueKey}
              checkInDate={checkIn}
              checkOutDate={checkOut}
              occupants={rooms}
              cancellationPolicy={pkg.roomRate.cancellationPolicy}
              hideExcludesFlights
              offerType={offer.type}
            />}
          </Group>
        </CSSBreakpoint>
        <Divider kind="primary" />
      </Group>
      <VillasVipServices />
      <div>
        <LargeContentToggle
          maxHeight={200}
          showChevron
          showLessText={intl.formatMessage({
            id: '-bWmfmae4zfFpRzeFEU9',
            defaultMessage: 'Read less',
          })}
          showMoreText={intl.formatMessage({
            id: 'QjhuSIVg7qfomYyGPmII',
            defaultMessage: 'Read more',
          })}
        >
          <MarkdownRender type="compact" fontSize="medium" content={offer.description} />
        </LargeContentToggle>
      </div>
      {showTasteOfGoldPromo && <TasteOfGoldPromoCard>
        <CSSBreakpoint max="mobile">
          <TasteOfGoldPromoContent layout="vertical" size="medium" context="offerPage" />
        </CSSBreakpoint>
        <CSSBreakpoint min="tablet">
          <TasteOfGoldPromoContent layout="horizontal" size="medium" context="offerPage" />
        </CSSBreakpoint>
      </TasteOfGoldPromoCard>}
      <ContactUSP />
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    pkg: getCheapestSelectedPackageOptionWithDefault(appState, state).package,
    checkIn: state.checkInDate,
    checkOut: state.checkOutDate,
    rooms: state.rooms,
  }
}, VillasPageIntro)
